<div class="parking-dashboard">
  <!-- Date Navigation -->
  <div
    styleClass="bg-white/90"
    class="pb-6"
  >
    <div class="flex items-center justify-between">
      <p-button
        [label]="'parking.overview.previousWeek' | translate"
        icon="pi pi-chevron-left"
        severity="secondary"
        size="small"
        (onClick)="goToPreviousWeek()"
        class="min-w-[120px]"
      >
      </p-button>

      <div class="text-center">
        <div class="text-lg font-semibold">
          {{ formatDateRange() }}
        </div>
        <div class="text-sm text-gray-500">{{ formatYear() }} • {{ 'parking.overview.tableTitle' | translate }}</div>
      </div>

      <p-button
        [label]="'parking.overview.nextWeek' | translate"
        icon="pi pi-chevron-right"
        iconPos="right"
        severity="secondary"
        size="small"
        (onClick)="goToNextWeek()"
        class="min-w-[120px]"
      >
      </p-button>
    </div>

    @if (!isAtOriginalWindow()) {
      <div class="mt-4 flex justify-center">
        <p-button
          [label]="'parking.overview.originalPeriod' | translate"
          icon="pi pi-calendar"
          severity="info"
          size="small"
          (onClick)="goToToday()"
        >
        </p-button>
      </div>
    }
  </div>

  <!-- Compact Heatmap Table -->
  <div
    styleClass="border-2 bg-white/90 backdrop-blur-sm"
    class="pb-6"
  >
    <ng-template pTemplate="header">
      <div class="flex items-center justify-center gap-3 text-2xl font-semibold">{{ 'parking.overview.tableTitle' | translate }}</div>
    </ng-template>

    <div class="overflow-x-auto">
      @if (loading) {
        <div class="flex justify-center p-4">
          <div class="text-center text-gray-500">
            <i class="pi pi-spin pi-spinner text-2xl"></i>
            <p class="mt-2">{{ 'parking.overview.loading' | translate }}</p>
          </div>
        </div>
      } @else if (allLevels && allLevels.length > 0 && buildingData && buildingData.length > 0) {
        <p-table
          [value]="allLevels"
          styleClass="p-datatable-sm"
        >
          <ng-template pTemplate="header">
            <tr>
              <td class="border-b p-3 text-left font-semibold"></td>

              <!-- Week columns -->
              @for (day of buildingData; track day.date; let i = $index) {
                <td [class]="'min-w-[80px] border-b p-2 text-center font-medium ' + (isToday(day.date) ? 'relative' : '')">
                  <!-- Reserve space for TODAY badge to maintain consistent alignment -->
                  <div class="mb-1 h-5">
                    @if (isToday(day.date)) {
                      <div class="rounded bg-blue-200 px-2 py-1 text-xs font-bold text-blue-600 dark:bg-blue-800 dark:text-blue-300">
                        {{ 'parking.overview.today' | translate }}
                      </div>
                    }
                  </div>
                </td>
              }
            </tr>
            <tr>
              <th class="border-b p-3 text-left align-top font-semibold">{{ 'parking.overview.parkingSpace' | translate }}</th>

              <!-- Week columns -->
              @for (day of buildingData; track day.date; let i = $index) {
                <th
                  [class]="
                    'min-w-[80px] border-b p-2 text-center align-top font-medium' +
                    (isToday(day.date) ? 'relative border-blue-300 bg-blue-100 shadow-md dark:border-blue-600 dark:bg-blue-900' : '')
                  "
                >
                  <!-- Day name and date - always aligned -->
                  <div class="text-xs text-gray-500">{{ day.dayName }}</div>
                  <div class="text-sm font-semibold">{{ day.displayDate }}</div>
                  <!-- Reserve space for week number to maintain consistent alignment -->
                  <div class="mt-1 h-4">
                    @if (i === 0) {
                      <div class="text-xs font-bold text-indigo-600">{{ weekNumber }}. {{ 'parking.overview.week' | translate }}</div>
                    }
                  </div>
                </th>
              }
            </tr>
          </ng-template>
          <ng-template
            pTemplate="body"
            let-levelInfo
          >
            <tr class="border-b hover:bg-gray-50/50">
              <td class="p-3">
                <div class="flex items-center gap-2">
                  <div class="flex items-center gap-1">
                    <span class="rounded bg-indigo-100 px-2 py-1 text-sm font-medium text-indigo-700">
                      {{ levelInfo.buildingName.charAt(0) }}
                    </span>
                    <span class="text-sm font-medium"> {{ levelInfo.levelName }} ({{ levelInfo.totalSpaces }}) </span>
                  </div>
                </div>
              </td>

              <!-- Week data cells -->
              @for (day of buildingData; track day.date) {
                <td
                  [class]="
                    'p-2 text-center ' +
                    (isToday(day.date) ? 'border-l-2 border-r-2 border-blue-300 bg-blue-100 shadow-sm dark:border-blue-600 dark:bg-blue-900' : '')
                  "
                >
                  @if (getLevelData(day, levelInfo); as level) {
                    <div
                      [class]="
                        'flex cursor-pointer items-center justify-center rounded px-2 py-1 text-sm font-semibold transition-transform hover:scale-105 ' +
                        getHeatmapClass(level.freeSpaces, level.totalSpaces)
                      "
                      [title]="
                        level.occupiedSpaces +
                        ' ' +
                        ('parking.overview.occupiedSpaces' | translate) +
                        ' / ' +
                        level.totalSpaces +
                        ' ' +
                        ('parking.overview.totalSpaces' | translate) +
                        ' (' +
                        level.occupancyRate +
                        '% ' +
                        ('parking.overview.occupied' | translate) +
                        ')'
                      "
                    >
                      {{ level.occupiedSpaces }}
                    </div>
                  } @else {
                    <div class="flex items-center justify-center rounded bg-gray-200 px-2 py-1 text-sm text-gray-500">-</div>
                  }
                </td>
              }
            </tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr class="border-t-2 border-gray-300 bg-gray-50 font-semibold dark:bg-gray-800">
              <td class="p-3 text-left">
                <span class="text-sm font-bold text-gray-700 dark:text-gray-300">{{ 'parking.overview.footer.utilization' | translate }}</span>
              </td>

              <!-- Week footer cells -->
              @for (day of buildingData; track day.date) {
                <td
                  [class]="
                    'p-2 text-center ' +
                    (isToday(day.date) ? 'border-l-2 border-r-2 border-blue-300 bg-blue-100 dark:border-blue-600 dark:bg-blue-900' : '')
                  "
                >
                  <div class="flex items-center justify-center text-sm font-bold text-gray-700 dark:text-gray-300">
                    {{ getDayOccupancyPercentage(day) }}<span class="text-xs font-normal opacity-75">%</span>
                  </div>
                </td>
              }
            </tr>
          </ng-template>
        </p-table>
      } @else {
        <div class="flex justify-center p-4">
          <div class="text-center text-gray-500">
            <i class="pi pi-info-circle text-2xl"></i>
            <p class="mt-2">{{ 'parking.overview.noData' | translate }}</p>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Legend -->
  <div
    styleClass="bg-white/80 backdrop-blur-sm"
    class="pb-4"
  >
    <div class="flex flex-wrap justify-center gap-6">
      <div class="flex items-center gap-2">
        <div class="flex h-6 w-6 items-center justify-center rounded bg-green-500 text-xs font-bold text-white">0</div>
        <span class="text-sm">{{ 'parking.overview.legend.empty' | translate }}</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="flex h-6 w-6 items-center justify-center rounded bg-green-400 text-xs font-bold text-gray-800">2</div>
        <span class="text-sm">{{ 'parking.overview.legend.lowOccupancy' | translate }}</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="flex h-6 w-6 items-center justify-center rounded bg-amber-400 text-xs font-bold text-gray-800">3</div>
        <span class="text-sm">{{ 'parking.overview.legend.highOccupancy' | translate }}</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="flex h-6 w-6 items-center justify-center rounded bg-red-500 text-xs font-bold text-white">5</div>
        <span class="text-sm">{{ 'parking.overview.legend.full' | translate }}</span>
      </div>
    </div>
    <div class="mt-4 text-center text-sm text-gray-500">{{ 'parking.overview.legend.description' | translate }}</div>
  </div>

  <!-- Summary Stats -->
  <div class="mb-4 mt-8">
    <h2 class="text-center text-xl font-bold text-gray-700 dark:text-gray-300">{{ 'parking.overview.stats.title' | translate }}</h2>
  </div>

  <div class="grid gap-6 p-4 pb-8 sm:grid-cols-3 lg:p-4">
    <!-- Today Occupied Spaces Card -->
    <app-stat-card
      [value]="getTodayOccupiedDisplay()"
      [label]="'parking.overview.stats.currentOccupied' | translate"
      icon="pi-lock"
      color="amber"
    ></app-stat-card>

    <!-- Weekly Utilization Average Card -->
    <app-stat-card
      [value]="getWeeklyOccupancyPercentage()"
      [label]="'parking.overview.stats.utilizationAvg' | translate"
      icon="pi-chart-bar"
      color="green"
      suffix="%"
    ></app-stat-card>

    <!-- Monthly Utilization Average Card -->
    <app-stat-card
      [value]="getMonthlyUtilizationDisplay()"
      [label]="'parking.overview.stats.monthlyUtilizationAvg' | translate"
      icon="pi-calendar"
      color="blue"
    ></app-stat-card>
  </div>
</div>
